import { useState, useEffect, useRef, useCallback } from 'react'
import { useOvacsNotificationContext } from '../contexts/OvacsNotificationContext'
import { tokenStorage } from '../utils/crypto'
import { useAuth } from '../contexts/AuthContext'
import apiConfig from '../config/apiConfig'
import apiService from '../services/api'

/**
 * Custom hook for Ovacs unified notification system with SSE streaming
 *
 * Features:
 * - Real-time SSE streaming (< 1 second delivery)
 * - Automatic reconnection with exponential backoff
 * - Workspace switching support
 * - Browser notification API integration
 * - Toast notifications
 * - Unread count tracking
 * - API methods for mark as read/unread, bulk actions
 *
 * @param {Object} showToast - Toast notification functions (optional)
 * @returns {Object} Notification state and methods
 */
const useOvacsNotifications = (showToast) => {
  const { user, ownerAccountId } = useAuth()
  const context = useOvacsNotificationContext()

  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected') // 'disconnected', 'connecting', 'connected', 'error'

  const eventSourceRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5
  const baseReconnectDelay = 2000 // 2 seconds

  /**
   * Fetch initial notifications (fallback for when SSE is not connected)
   */
  const fetchInitialNotifications = useCallback(async () => {
    try {
      context.setLoading(true)
      const response = await apiService.get('/ovacs-notifications/', {
        params: { page: 1, page_size: 50 }
      })

      const notifications = response.data?.results || []
      const unreadCount = response.data?.unread_count || 0

      context.setNotifications(notifications, unreadCount)
    } catch (err) {
      console.error('Error fetching initial notifications:', err)
      context.setError(err.message || 'Failed to fetch notifications')
    }
  }, [context])

  /**
   * Fetch unread count
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.get('/ovacs-notifications/unread-count/')
      const count = response.data?.unread_count || 0
      context.setUnreadCount(count)
      return count
    } catch (err) {
      console.error('Error fetching unread count:', err)
      return context.unreadCount
    }
  }, [context])

  /**
   * Fetch notification statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.get('/ovacs-notifications/stats/')
      context.setStats(response.data)
      return response.data
    } catch (err) {
      console.error('Error fetching notification stats:', err)
      return null
    }
  }, [context])

  /**
   * Connect to SSE stream
   */
  const connectToStream = useCallback(() => {
    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const token = tokenStorage.getToken()
    const userInfo = tokenStorage.getUserInfo()

    if (!token || !user) {
      console.log('No auth token or user available for SSE connection')
      return
    }

    // Build SSE URL with authentication.
    // Notifications are workspace-scoped: each workspace has its own tray.
    // Forward the current owner_account_id so followers receive notifications
    // emitted in the workspace they're currently viewing (not only in their own).
    const API_BASE_URL = apiConfig.API_BASE_URL
    let streamURL = `${API_BASE_URL}/ovacs-notifications/stream/?token=${token}`
    if (ownerAccountId) {
      streamURL += `&owner_account_id=${ownerAccountId}`
    }

    console.log('🔌 [OvacsNotification] Connecting to SSE stream...')
    setConnectionStatus('connecting')

    try {
      const eventSource = new EventSource(streamURL)
      eventSourceRef.current = eventSource

      // Handle connection opened
      eventSource.addEventListener('open', () => {
        console.log('✅ [OvacsNotification] SSE stream connected')
        setIsConnected(true)
        setConnectionStatus('connected')
        context.clearError()
        reconnectAttempts.current = 0 // Reset reconnect attempts on successful connection
      })

      // Handle system events (connected, keepalive, error)
      eventSource.addEventListener('system', (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('📡 [OvacsNotification] System event:', data.type)

          if (data.type === 'connected') {
            // Fetch initial notifications when connected
            fetchInitialNotifications()
          } else if (data.type === 'error') {
            console.error('❌ [OvacsNotification] Server error:', data.message)
            context.setError(data.message)
          }
        } catch (err) {
          console.error('Error parsing system event:', err)
        }
      })

      // Handle notification events (THE MAIN EVENT!)
      eventSource.addEventListener('notification', (event) => {
        try {
          const notification = JSON.parse(event.data)
          console.log('🔔 [OvacsNotification] New notification received:', notification)

          // Add notification to context (will update unread count automatically)
          context.addNotification(notification)

          // Show toast notification (optional - if callback provided)
          if (showToast) {
            showToastNotification(notification)
          } 

          // Show browser notification (if permission granted)
          showBrowserNotification(notification)

        } catch (err) {
          console.error('Error parsing notification event:', err)
        }
      })

      // Handle errors
      eventSource.addEventListener('error', (event) => {
        console.error('❌ [OvacsNotification] SSE connection error:', event)
        setIsConnected(false)
        setConnectionStatus('error')

        // EventSource automatically tries to reconnect, but we can add our own logic
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('Connection closed, attempting manual reconnect...')
          attemptReconnect()
        }
      })

    } catch (err) {
      console.error('Error creating EventSource:', err)
      context.setError(err.message)
      setConnectionStatus('error')
      attemptReconnect()
    }
  }, [user, ownerAccountId, fetchInitialNotifications, context, showToast])

  /**
   * Attempt to reconnect with exponential backoff
   */
  const attemptReconnect = useCallback(() => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.error('Max reconnection attempts reached. Falling back to polling.')
      context.setError('Connection lost. Please refresh the page.')
      setConnectionStatus('error')
      return
    }

    reconnectAttempts.current += 1
    const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.current - 1)

    console.log(
      `⏳ [OvacsNotification] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`
    )

    reconnectTimeoutRef.current = setTimeout(() => {
      connectToStream()
    }, delay)
  }, [connectToStream, context])

  /**
   * Show toast notification (integrate with your toast system)
   */
  const showToastNotification = (notification) => {
    if (!showToast) return

    const isBroadcast = notification.notification_type === 'broadcast'
    const isUrgent = notification.priority === 'urgent'
    const isHigh = notification.priority === 'high'

    // Choose toast type based on priority/type
    if (isUrgent) {
      showToast.error && showToast.error(notification.message, notification.title)
    } else if (isHigh) {
      showToast.warning && showToast.warning(notification.message, notification.title)
    } else if (isBroadcast) {
      showToast.info && showToast.info(notification.message, notification.title)
    } else {
      showToast.success && showToast.success(notification.message, notification.title)
    }
  }

  /**
   * Show browser notification (if permission granted)
   */
  const showBrowserNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      // Don't show browser notification for low priority
      if (notification.priority === 'low') return

      const browserNotif = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: `ovacs-notification-${notification.id}`,
        requireInteraction: notification.priority === 'urgent', // Keep urgent notifications until dismissed
        data: {
          notificationId: notification.id,
          link: notification.link,
        }
      })

      // Handle click on browser notification
      browserNotif.onclick = () => {
        window.focus()
        if (notification.link) {
          window.location.href = notification.link
        }
        browserNotif.close()
      }
    }
  }

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }
    return Notification.permission === 'granted'
  }, [])

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      // Optimistic update
      context.markAsRead(notificationId)

      // API call
      await apiService.post(`/ovacs-notifications/${notificationId}/mark-read/`)

      console.log(`✅ [OvacsNotification] Marked notification #${notificationId} as read`)
    } catch (err) {
      console.error('Error marking notification as read:', err)
      // Revert optimistic update by fetching fresh data
      fetchInitialNotifications()
    }
  }, [context, fetchInitialNotifications])

  /**
   * Mark notification as unread
   */
  const markAsUnread = useCallback(async (notificationId) => {
    try {
      // Optimistic update
      context.markAsUnread(notificationId)

      // API call
      await apiService.post(`/ovacs-notifications/${notificationId}/mark-unread/`)

      console.log(`✅ [OvacsNotification] Marked notification #${notificationId} as unread`)
    } catch (err) {
      console.error('Error marking notification as unread:', err)
      // Revert optimistic update
      fetchInitialNotifications()
    }
  }, [context, fetchInitialNotifications])

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      // Optimistic update
      context.markAllAsRead()

      // API call
      await apiService.post('/ovacs-notifications/mark-all-read/')

      console.log('✅ [OvacsNotification] Marked all notifications as read')
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
      // Revert optimistic update
      fetchInitialNotifications()
    }
  }, [context, fetchInitialNotifications])

  /**
   * Bulk action (mark read, mark unread, delete)
   */
  const bulkAction = useCallback(async (notificationIds, action) => {
    try {
      // Optimistic update
      if (action === 'mark_read') {
        context.markMultipleAsRead(notificationIds)
      } else if (action === 'mark_unread') {
        context.markMultipleAsUnread(notificationIds)
      } else if (action === 'delete') {
        notificationIds.forEach(id => context.removeNotification(id))
      }

      // API call
      await apiService.post('/ovacs-notifications/bulk-action/', {
        notification_ids: notificationIds,
        action: action
      })

      console.log(`✅ [OvacsNotification] Bulk action '${action}' completed for ${notificationIds.length} notifications`)
    } catch (err) {
      console.error('Error performing bulk action:', err)
      // Revert optimistic update
      fetchInitialNotifications()
    }
  }, [context, fetchInitialNotifications])

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      // Optimistic update
      context.removeNotification(notificationId)

      // API call
      await apiService.delete(`/ovacs-notifications/${notificationId}/delete/`)

      console.log(`✅ [OvacsNotification] Deleted notification #${notificationId}`)
    } catch (err) {
      console.error('Error deleting notification:', err)
      // Revert optimistic update
      fetchInitialNotifications()
    }
  }, [context, fetchInitialNotifications])

  /**
   * Disconnect from stream
   */
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('🔌 [OvacsNotification] Disconnecting from SSE stream')
      eventSourceRef.current.close()
      eventSourceRef.current = null
      setIsConnected(false)
      setConnectionStatus('disconnected')
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
  }, [])

  /**
   * Refresh notifications (manual fetch)
   */
  const refresh = useCallback(async () => {
    await fetchInitialNotifications()
    await fetchUnreadCount()
  }, [fetchInitialNotifications, fetchUnreadCount])

  // Connect on mount and reconnect whenever the workspace (ownerAccountId)
  // changes so the SSE stream is always keyed to the workspace the user is
  // currently viewing. Notifications are workspace-scoped.
  useEffect(() => {
    if (user) {
      connectToStream()
    }

    // Cleanup on unmount or before the next connection
    return () => {
      disconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, ownerAccountId])

  // Request browser notification permission on first load
  useEffect(() => {
    requestNotificationPermission()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    // State from context
    notifications: context.notifications,
    unreadCount: context.unreadCount,
    isLoading: context.isLoading,
    error: context.error,
    stats: context.stats,

    // Connection state
    isConnected,
    connectionStatus,

    // Methods
    markAsRead,
    markAsUnread,
    markAllAsRead,
    bulkAction,
    deleteNotification,
    refresh,
    fetchUnreadCount,
    fetchStats,
    requestNotificationPermission,
    disconnect,

    // Context methods (direct access)
    getUnreadNotifications: context.getUnreadNotifications,
    getNotificationsByType: context.getNotificationsByType,
    getNotificationsByPriority: context.getNotificationsByPriority,
    getUrgentNotifications: context.getUrgentNotifications,
  }
}

export default useOvacsNotifications
